class SwatchOverflow extends HTMLElement {
  constructor() {
    super();
    this.moreButton = null;
    this.resizeObserver = null;
    this.intersectionObserver = null;
    this.observedContainer = null;
    this.expanded = false;
    this.handleMoreClick = this.handleMoreClick.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.handleWindowLoad = this.handleWindowLoad.bind(this);
    this.scheduleUpdate = this.scheduleUpdate.bind(this);
  }

  connectedCallback() {
    this.swatches = Array.from(
      this.querySelectorAll('.swatch-related-products__item'),
    );
    if (!this.swatches.length) return;

    this.debugId =
      this.closest('swatch-related-products')?.dataset.swatchInstanceId ||
      'unknown';

    this.ensureMoreButton();
    this.scheduleUpdate();
    setTimeout(() => this.scheduleUpdate(), 120);
    setTimeout(() => this.scheduleUpdate(), 400);
    setTimeout(() => this.scheduleUpdate(), 900);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
      this.resizeObserver.observe(this);

      this.observedContainer = this.closest(
        '.card-wrapper, .card, .grid__item, .product-grid',
      );
      if (this.observedContainer) {
        this.resizeObserver.observe(this.observedContainer);
      }
    } else {
      window.addEventListener('resize', this.handleWindowResize);
    }

    window.addEventListener('resize', this.handleWindowResize);
    window.addEventListener('orientationchange', this.handleWindowResize);
    window.addEventListener('load', this.handleWindowLoad);

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.scheduleUpdate();
        });
      });
      this.intersectionObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.moreButton?.removeEventListener('click', this.handleMoreClick);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    window.removeEventListener('resize', this.handleWindowResize);
    window.removeEventListener('orientationchange', this.handleWindowResize);
    window.removeEventListener('load', this.handleWindowLoad);
  }

  handleWindowResize() {
    this.scheduleUpdate();
  }

  handleWindowLoad() {
    this.scheduleUpdate();
  }

  scheduleUpdate() {
    requestAnimationFrame(() => this.updateOverflow());
  }

  ensureMoreButton() {
    if (this.moreButton) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'swatch-related-products__more';
    button.setAttribute('aria-expanded', 'false');
    button.addEventListener('click', this.handleMoreClick);
    this.appendChild(button);
    this.moreButton = button;
  }

  handleMoreClick() {
    this.expanded = true;
    this.updateOverflow();
  }

  updateOverflow() {
    if (!this.swatches?.length || !this.moreButton) return;

    // Skip measurements while the component has no layout box yet.
    if (this.offsetParent === null || this.clientWidth === 0) return;

    this.swatches.forEach((item) => item.classList.remove('is-hidden'));

    if (this.expanded) {
      this.moreButton.classList.remove('is-visible');
      this.moreButton.setAttribute('aria-expanded', 'true');
      return;
    }

    this.moreButton.classList.remove('is-visible');
    this.moreButton.textContent = '';
    this.moreButton.setAttribute('aria-expanded', 'false');

    const firstRowTop = this.swatches[0].offsetTop;
    const overflowed = this.swatches.filter(
      (item) => item.offsetTop > firstRowTop,
    );

    if (!overflowed.length) return;

    overflowed.forEach((item) => item.classList.add('is-hidden'));

    // Reserve one first-row slot for the "+N" trigger so it does not wrap.
    const firstRowVisible = this.swatches.filter(
      (item) =>
        item.offsetTop === firstRowTop && !item.classList.contains('is-hidden'),
    );

    const reserveSlotCandidate =
      [...firstRowVisible]
        .reverse()
        .find((item) => !item.querySelector('input:checked')) ||
      firstRowVisible[firstRowVisible.length - 1];

    if (reserveSlotCandidate) {
      reserveSlotCandidate.classList.add('is-hidden');
      overflowed.push(reserveSlotCandidate);
    }

    this.moreButton.textContent = `+${overflowed.length}`;
    this.moreButton.classList.add('is-visible');
  }
}

console.log('[SwatchRelatedProducts] script loaded');

class SwatchRelatedProducts extends HTMLElement {
  connectedCallback() {
    console.log('[SwatchRelatedProducts] connected', {
      element: this,
      sectionId: this.dataset.sectionId || null,
      radioCount: this.querySelectorAll('input[type="radio"]').length,
    });
    this.addEventListener('change', this.handleChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
  }

  handleChange = async (event) => {
    const target = event.target;
    if (!target.matches('input[type="radio"]')) return;

    const selectedProductUrl = target.dataset.productUrl;
    console.log('[SwatchRelatedProducts] change', {
      target,
      selectedProductUrl,
      selectedProductId: target.dataset.productId || null,
      selectedProductHandle: target.dataset.productHandle || null,
    });
    if (!selectedProductUrl) return;

    const quickAddModal =
      target.closest('quick-add-modal, modal-dialog.quick-add-modal') ||
      this.closest('quick-add-modal, modal-dialog.quick-add-modal') ||
      target
        .closest('[id^="QuickAddInfo-"]')
        ?.closest('quick-add-modal, modal-dialog.quick-add-modal');
    if (quickAddModal) {
      console.log('[SwatchRelatedProducts] branch: quick-add-modal', {
        modalId: quickAddModal.id || null,
        modalTag: quickAddModal.tagName,
      });
      try {
        await this.reloadQuickAddModalContent(
          quickAddModal,
          selectedProductUrl,
        );
        console.log('[SwatchRelatedProducts] quick-add-modal reload complete');
      } catch (error) {
        console.error(
          '[SwatchRelatedProducts] Quick add modal reload failed:',
          error,
        );
      }
      return;
    }

    const currentCard = this.closest('.card-wrapper[data-card-product-handle]');
    const isProductPageContext =
      document.body.classList.contains('product') ||
      Boolean(
        this.closest('main-product, .product__info-container, product-info'),
      );

    if (isProductPageContext && !currentCard) {
      console.log(
        '[SwatchRelatedProducts] branch: explicit product page redirect',
        {
          selectedProductUrl,
        },
      );
      window.location.assign(selectedProductUrl);
      return;
    }

    if (!currentCard) {
      console.log('[SwatchRelatedProducts] branch: product page redirect', {
        selectedProductUrl,
      });
      window.location.assign(selectedProductUrl);
      return;
    }

    console.log('[SwatchRelatedProducts] branch: card patch', {
      cardHandle: currentCard.dataset.cardProductHandle || null,
      cardProductId: currentCard.dataset.cardProductId || null,
    });
    currentCard.classList.add('is-loading');

    try {
      const productData = this.getProductDataFromRadio(target);
      console.log('[SwatchRelatedProducts] productData payload', productData);
      if (!productData) return;

      this.patchCardContents(currentCard, productData);
      console.log('[SwatchRelatedProducts] card patch complete');
    } catch (error) {
      console.error('[SwatchRelatedProducts] Card update failed:', error);
      window.location.href = selectedProductUrl;
    } finally {
      currentCard.classList.remove('is-loading');
    }
  };

  getProductDataFromRadio(radio) {
    const payloadNode = radio
      .closest('.swatch-related-products__item')
      ?.querySelector('.swatch-related-products__product-data');
    console.log('[SwatchRelatedProducts] payload node', payloadNode);
    if (!payloadNode) return null;

    try {
      return JSON.parse(payloadNode.textContent);
    } catch (error) {
      return null;
    }
  }

  patchCardContents(card, productData) {
    if (!productData?.url) return;

    card.dataset.cardProductId = String(productData.id);
    card.dataset.cardProductHandle = productData.handle;

    card.querySelectorAll('a[href*="/products/"]').forEach((anchor) => {
      anchor.href = productData.url;
    });

    card.querySelectorAll('[data-product-url]').forEach((element) => {
      element.setAttribute('data-product-url', productData.url);
    });

    card
      .querySelectorAll('.card__heading .full-unstyled-link')
      .forEach((link) => {
        link.textContent = productData.title;
        link.href = productData.url;
      });

    this.updateCardImages(card, productData);
    this.updatePriceBlocks(card, productData.priceHtml);
    this.updateRatingBlock(card, productData.ratingHtml);
    this.updateQuickAddState(card, productData);
  }

  updateCardImages(card, productData) {
    const imageNodes = card.querySelectorAll('.card__media .media img');
    if (!imageNodes.length) return;

    const [primaryImage, secondaryImage] = imageNodes;

    if (primaryImage && productData.featuredImage) {
      primaryImage.src = productData.featuredImage;
      primaryImage.srcset = `${productData.featuredImage} 533w`;
      primaryImage.alt = productData.title || '';
    }

    if (secondaryImage) {
      if (productData.secondaryImage) {
        secondaryImage.src = productData.secondaryImage;
        secondaryImage.srcset = `${productData.secondaryImage} 533w`;
        secondaryImage.removeAttribute('hidden');
      } else {
        secondaryImage.setAttribute('hidden', '');
      }
    }
  }

  updatePriceBlocks(card, priceHtml) {
    if (!priceHtml) return;
    const existingPrices = card.querySelectorAll('.price');
    if (!existingPrices.length) return;

    existingPrices.forEach((priceNode) => {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = priceHtml.trim();
      const nextPrice = wrapper.querySelector('.price');
      if (nextPrice) {
        priceNode.replaceWith(nextPrice);
      }
    });
  }

  updateRatingBlock(card, ratingHtml) {
    const ratingContainer = card.querySelector('.card-rating-badge');
    if (!ratingContainer) return;

    ratingContainer.innerHTML = ratingHtml || '';
  }

  async reloadQuickAddModalContent(modalElement, productUrl) {
    console.log('[SwatchRelatedProducts] reloadQuickAddModalContent start', {
      modalId: modalElement?.id || null,
      productUrl,
    });
    const modalId = modalElement.id;
    const openerButton = modalId
      ? document.querySelector(
          `[data-modal="#${modalId}"] button[data-product-url]`,
        )
      : null;

    if (openerButton) {
      openerButton.setAttribute('data-product-url', productUrl);

      const modalOpener = openerButton.closest('modal-opener');
      if (modalOpener && typeof modalOpener.originalProductUrl === 'string') {
        modalOpener.originalProductUrl = productUrl;
      }
    }

    const productHandle = this.extractHandleFromProductUrl(productUrl);
    if (
      window.ProductModalLoader &&
      typeof window.ProductModalLoader.updateModalContent === 'function' &&
      modalId &&
      productHandle
    ) {
      console.log(
        '[SwatchRelatedProducts] using ProductModalLoader.updateModalContent',
        {
          modalId,
          productHandle,
        },
      );
      window.ProductModalLoader.updateModalContent(modalId, productHandle);
      return;
    }

    if (typeof modalElement.show === 'function' && openerButton) {
      console.log('[SwatchRelatedProducts] using modal.show fallback');
      modalElement.show(openerButton);
      return;
    }

    const modalContent = modalElement.querySelector('[id^="QuickAddInfo-"]');
    if (!modalContent) return;

    const response = await fetch(productUrl);
    console.log('[SwatchRelatedProducts] direct fetch fallback response', {
      ok: response.ok,
      status: response.status,
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch product content: ${response.status}`);
    }

    const responseText = await response.text();
    const responseHTML = new DOMParser().parseFromString(
      responseText,
      'text/html',
    );
    const productElement = responseHTML.querySelector('product-info');
    if (!productElement) return;

    if (typeof modalElement.preprocessHTML === 'function') {
      modalElement.preprocessHTML(productElement);
    } else {
      productElement.setAttribute('data-update-url', 'false');
    }

    if (
      typeof HTMLUpdateUtility !== 'undefined' &&
      HTMLUpdateUtility.setInnerHTML
    ) {
      HTMLUpdateUtility.setInnerHTML(modalContent, productElement.outerHTML);
    } else {
      modalContent.innerHTML = productElement.outerHTML;
    }
  }

  extractHandleFromProductUrl(productUrl) {
    try {
      const parsed = new URL(productUrl, window.location.origin);
      const productPathMatch = parsed.pathname.match(/\/products\/([^/?#]+)/);
      return productPathMatch ? decodeURIComponent(productPathMatch[1]) : null;
    } catch (error) {
      return null;
    }
  }

  updateQuickAddState(card, productData) {
    // Keep quick-add opener buttons pointed at the selected product.
    card.querySelectorAll('button[data-product-url]').forEach((button) => {
      button.setAttribute('data-product-url', productData.url);
    });

    // ModalOpener stores originalProductUrl at init and resets before open.
    // Update that internal value so it doesn't revert to the previous product.
    card.querySelectorAll('modal-opener').forEach((opener) => {
      if (typeof opener.originalProductUrl === 'string') {
        opener.originalProductUrl = productData.url;
      }
    });

    // Direct add-to-cart form for single-variant products.
    const variantInput = card.querySelector('.product-variant-id');
    if (variantInput && productData.firstVariantId) {
      variantInput.value = productData.firstVariantId;
    }

    // Ensure standard quick add modal fetches fresh content on next open.
    card
      .querySelectorAll('quick-add-modal [id^="QuickAddInfo-"]')
      .forEach((contentContainer) => {
        contentContainer.innerHTML = '';
      });

    this.updateBulkQuickAddModal(card, productData);
  }

  updateBulkQuickAddModal(card, productData) {
    const bulkModalRoot = card.querySelector(
      '.quick-add-modal__content-info--bulk',
    );
    if (!bulkModalRoot) return;

    bulkModalRoot
      .querySelectorAll('a[href*="/products/"]')
      .forEach((anchor) => {
        anchor.href = productData.url;
      });

    bulkModalRoot
      .querySelectorAll('.quick-add-modal__content-info--bulk-details h3')
      .forEach((title) => {
        title.textContent = productData.title || '';
      });

    const bulkImage = bulkModalRoot.querySelector(
      '.quick-add__product-container img',
    );
    if (bulkImage && productData.featuredImage) {
      bulkImage.src = productData.featuredImage;
      bulkImage.srcset = `${productData.featuredImage} 533w`;
      bulkImage.alt = productData.title || '';
    }

    if (productData.priceHtml) {
      const priceNodes = bulkModalRoot.querySelectorAll('.price');
      priceNodes.forEach((priceNode) => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = productData.priceHtml.trim();
        const nextPrice = wrapper.querySelector('.price');
        if (nextPrice) priceNode.replaceWith(nextPrice);
      });
    }

    bulkModalRoot.querySelectorAll('bulk-modal').forEach((bulkModal) => {
      bulkModal.dataset.url = productData.url;
      if (productData.id) {
        bulkModal.dataset.productId = String(productData.id);
      }
    });
  }
}

customElements.define('swatch-related-products', SwatchRelatedProducts);
customElements.define('swatch-overflow', SwatchOverflow);
